import type { z } from 'zod';
import { type Subject } from '../common/enums';
import { type TransformerConfig } from '../common/types';
import {
  createDecodeFromDbFn,
  createEncodeToDbFn,
  createRenameTransformer,
  createTransformer,
} from '../common/utils';
import { buildRecordId } from '../recordIds';
import {
  DBLegacyExamQuestionAnalyticsSchema,
  DBLegacyExamQuestionSchema,
  LegacyExamQuestionSchema,
} from './schemas';

type DBLegacyExamQuestion = z.infer<typeof DBLegacyExamQuestionSchema>;
type DBLegacyExamQuestionAnalytics = z.infer<typeof DBLegacyExamQuestionAnalyticsSchema>;
type LegacyExamQuestion = z.infer<typeof LegacyExamQuestionSchema>;

export const LegacyExamQuestionCodecConfig: TransformerConfig<
  typeof LegacyExamQuestionSchema,
  typeof DBLegacyExamQuestionSchema
> = {
  name: 'LegacyExamQuestion',
  clientSchema: LegacyExamQuestionSchema,
  dbSchema: DBLegacyExamQuestionSchema,
  transformers: [
    createRenameTransformer<typeof LegacyExamQuestionSchema, typeof DBLegacyExamQuestionSchema>(
      'id',
      'questionId'
    ),
    // subject, examCode ↔ subjectExamIdentifier: 'subject#examCode', and examId from the two
    createTransformer<typeof LegacyExamQuestionSchema, typeof DBLegacyExamQuestionSchema>({
      encoder: {
        // The exam's id is never stored, so its value is ignored
        sourceFields: ['subject', 'examCode', 'examId'] as const,
        encode: ({ subject, examCode }) => {
          if (subject === undefined && examCode === undefined) {
            return {};
          }
          if (subject === undefined || examCode === undefined) {
            throw new Error("A question's exam key needs both its subject and its exam code");
          }
          return {
            subjectExamIdentifier: `${subject as LegacyExamQuestion['subject']}#${examCode as LegacyExamQuestion['examCode']}`,
          };
        },
      },
      decoder: {
        sourceFields: ['subjectExamIdentifier'] as const,
        decode: ({ subjectExamIdentifier }) => {
          const [subject, examCode] = (
            subjectExamIdentifier as DBLegacyExamQuestion['subjectExamIdentifier']
          ).split('#') as [Subject, string];
          const examId = buildRecordId('legacyExam', { subject, code: examCode });
          return { subject, examCode, ...(examId !== undefined && { examId }) };
        },
      },
    }),
    // labelParts: ['1', 'a', 'iv'] ↔ label: '1#a#iv'
    createTransformer<typeof LegacyExamQuestionSchema, typeof DBLegacyExamQuestionSchema>({
      encoder: {
        sourceFields: ['labelParts'] as const,
        encode: ({ labelParts }) => ({
          label: (labelParts as LegacyExamQuestion['labelParts']).join('#'),
        }),
      },
      decoder: {
        sourceFields: ['label'] as const,
        decode: ({ label }) => ({
          labelParts: (label as DBLegacyExamQuestion['label']).split(
            '#'
          ) as LegacyExamQuestion['labelParts'],
        }),
      },
    }),
    // active: boolean ↔ active: 'true' | 'false'
    createTransformer<typeof LegacyExamQuestionSchema, typeof DBLegacyExamQuestionSchema>({
      encoder: {
        sourceFields: ['active'] as const,
        encode: ({ active }) => ({
          active: (active as LegacyExamQuestion['active']) ? 'true' : 'false',
        }),
      },
      decoder: {
        sourceFields: ['active'] as const,
        decode: ({ active }) => ({
          active: (active as DBLegacyExamQuestion['active']) === 'true',
        }),
      },
    }),
    // analytics: compute average on decode, strip average on encode
    createTransformer<typeof LegacyExamQuestionSchema, typeof DBLegacyExamQuestionSchema>({
      encoder: {
        sourceFields: ['analytics'] as const,
        encode: ({ analytics }) => {
          const { average: _, ...dbAnalytics } = analytics as LegacyExamQuestion['analytics'];
          return { analytics: dbAnalytics };
        },
      },
      decoder: {
        sourceFields: ['analytics'] as const,
        decode: ({ analytics }) => {
          const dbAnalytics = analytics as DBLegacyExamQuestionAnalytics;
          return {
            analytics: {
              ...dbAnalytics,
              average:
                dbAnalytics.uniqueAttempts > 0
                  ? dbAnalytics.aggregateMark / dbAnalytics.uniqueAttempts
                  : 0,
            },
          };
        },
      },
    }),
    // topicCodes: [topicCode] ↔ topics: { topicCode: {} }
    createTransformer<typeof LegacyExamQuestionSchema, typeof DBLegacyExamQuestionSchema>({
      encoder: {
        sourceFields: ['topicCodes'] as const,
        encode: ({ topicCodes }) => ({
          topics: Object.fromEntries(
            (topicCodes as LegacyExamQuestion['topicCodes']).map(topicCode => [topicCode, {}])
          ),
        }),
      },
      decoder: {
        sourceFields: ['topics'] as const,
        decode: ({ topics }) => ({
          topicCodes: Object.keys(topics as DBLegacyExamQuestion['topics']),
        }),
      },
    }),
  ],
};

export const encodeLegacyExamQuestionToDb = createEncodeToDbFn(LegacyExamQuestionCodecConfig);
export const decodeLegacyExamQuestionFromDb = createDecodeFromDbFn(LegacyExamQuestionCodecConfig);
